import { ApplicationDeadline } from "./ApplicationDeadline";
import { Link } from "wouter";
import type { ReactNode } from "react";
import { ArrowLeft } from "lucide-react";
import { Skeleton } from "@sfc/ui";

// Publish the actual sticky obstruction, including wrapped titles and status text.
export function measureApplicationHeader(header: HTMLElement | null) {
  const parent = header?.parentElement;

  if (!header || !parent) {
    return;
  }

  const measure = () => {
    parent.style.setProperty(
      "--application-header-height",
      `${header.getBoundingClientRect().height}px`,
    );
  };

  measure();
  const observer = new ResizeObserver(measure);
  observer.observe(header);

  return () => {
    observer.disconnect();
    parent.style.removeProperty("--application-header-height");
  };
}

export function ApplicationHeader({
  roundName,
  opensAt,
  closesAt,
  teamName,
  teamId,
  teamAction,
  description,
  backLabel = "Application details",
  action,
  onBack,
  backDisabled = false,
  status,
}: {
  roundName?: string;
  opensAt?: string | null;
  closesAt?: string | null;
  teamName?: string;
  teamId?: string;
  teamAction?: ReactNode;
  description?: ReactNode;
  backLabel?: string;
  action?: ReactNode;
  onBack?: () => void;
  backDisabled?: boolean;
  status?: ReactNode;
}) {
  return (
    <header
      ref={measureApplicationHeader}
      className="sticky top-0 z-10 space-y-4 border-b border-border bg-background py-4"
    >
      <div className="flex min-h-9 items-center justify-between gap-4">
        <button
          type="button"
          onClick={onBack}
          disabled={backDisabled || !onBack}
          className="inline-flex min-h-9 items-center gap-2 rounded-sm body-sm text-primary hover:underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-50 disabled:no-underline"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          {backLabel}
        </button>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-8">
        <div className="min-w-0 flex-1 space-y-2">
          {roundName ? (
            <h1 className="heading-lg [overflow-wrap:anywhere]">{roundName}</h1>
          ) : (
            <Skeleton
              className="h-8 w-2/3 motion-reduce:animate-none"
              aria-hidden="true"
            />
          )}
          <ApplicationDeadline opensAt={opensAt} closesAt={closesAt} />
          {teamName ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <p className="min-w-0 body-sm text-muted-foreground [overflow-wrap:anywhere]">
                Applying as{" "}
                {teamId ? (
                  <Link
                    href={`/teams/${encodeURIComponent(teamId)}`}
                    className="ml-1 font-medium text-foreground underline underline-offset-4"
                  >
                    {teamName}
                  </Link>
                ) : (
                  <span className="ml-1 font-medium text-foreground">
                    {teamName}
                  </span>
                )}
              </p>
              {teamAction}
            </div>
          ) : (
            <Skeleton
              className="h-5 w-40 motion-reduce:animate-none"
              aria-hidden="true"
            />
          )}
          {description && (
            <p className="body-sm text-muted-foreground">{description}</p>
          )}
        </div>
        <div className="flex max-w-sm flex-col gap-2 body-sm text-muted-foreground sm:items-end sm:text-right">
          {action}
          {status ?? (
            <Skeleton
              className="h-5 w-16 motion-reduce:animate-none"
              aria-hidden="true"
            />
          )}
        </div>
      </div>
    </header>
  );
}
